import type Account from '@/data/account/model';
import { getAccountPlan } from '@/data/account/selectors';
import { fetchFreeTrialStatus } from '@/data/freetrial/actions/fetchFreeTrialStatus';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import Popover from '@/shared/ui/Popover';
import { SmartLink } from '@/shared/util/redirect';
import calendarImg from '@/styles/assets/img/new-schedule.svg';
import '@/shared/upsell/assets/styles/upgradePopover.scss';
import WithParam from '@/billing/util/WithParam';
import FreeTrial from '@/data/freetrial/model';
import getFreeTrialStatus from '@/data/freetrial/selectors/getFreeTrialStatus';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { useLocation } from 'react-router-dom';
import { Actions } from '@/shared/experiment/constants';
import Mercury, { GROWTH_UPSELL } from '@/shared/util/mercury';
import { useWiwDispatch, useWiwSelector } from '@/store';

type Props = PropsWithChildren;

export const UpgradePopover = ({ children }: Props) => {
  const COOKIE = 'navUpsellPopover';
  const user = useWiwSelector(getAuthUser);
  const account: Account = useWiwSelector(getAuthAccount);
  const accountPlan = useWiwSelector(getAccountPlan);
  const trialStatus = useWiwSelector(getFreeTrialStatus);
  const dispatch = useWiwDispatch();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchFreeTrialStatus());
  }, [dispatch]);

  useEffect(() => {
    if (
      accountPlan.isFreemium() &&
      user.canManage() &&
      moment().diff(moment(account.created_at), 'days') >= 2 &&
      cookieIsOldOrUnset() &&
      !excludedRoute() &&
      trialStatus.toJS().status === FreeTrial.STATUS.UNAVAILABLE
    ) {
      setCookie();
      trackUpsell(Actions.MODAL_SHOWN);
      setIsOpen(true);
    }
  }, [accountPlan, user, trialStatus]);

  const cookieIsOldOrUnset = () => {
    const cookie = Cookies.get(COOKIE);

    if (cookie !== undefined) {
      const cookieTime = moment(cookie);
      const now = moment();
      return cookieTime.isBefore(now);
    }
    return true;
  };

  const setCookie = () => {
    const cookieTime = moment();

    cookieTime.add('15', 'days');
    Cookies.set(COOKIE, cookieTime.toISOString());
  };

  const excludedRoute = () => {
    const excludedRoutes = [account.billingUrl()];
    return excludedRoutes.includes(location.pathname);
  };

  const trackUpsell = (action: string) => {
    const fields = {
      action: action,
      featureRequested: WithParam.fromString('navUpsellPopover'),
    };

    Mercury.track(GROWTH_UPSELL, fields);
  };

  const onUpgradeNow = () => {
    trackUpsell(Actions.CLICKED_UPGRADE);
    setIsOpen(false);
  };

  const onNoThanks = () => {
    trackUpsell(Actions.CLICKED_CLOSE);
    setIsOpen(false);
  };

  return (
    <Popover width={355} position="bottom-start" shadow="md" opened={isOpen} withArrow={false}>
      <Popover.Target>{children}</Popover.Target>
      <Popover.Dropdown className="upsell-upgrade-popover-body">
        <div>
          <img alt="calendar" src={calendarImg} />
        </div>
        <div>
          <p>Hey {user.first_name}, you're currently on a limited free account.</p>
        </div>
        <div>
          <span>Unlock limitless scheduling and more great features!</span>
        </div>
        <div className="upsell-btn-container">
          <SmartLink to={account.plansUrl()} className="btn btn-primary" onClick={onUpgradeNow}>
            UPGRADE NOW
          </SmartLink>
          <button type="button" className="btn btn-secondary" onClick={onNoThanks}>
            NO, THANKS
          </button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};
